import { ArrowsClockwise, Sparkle, X } from "@phosphor-icons/react";
import { Button, Flex, Text } from "@radix-ui/themes";
import { useRouterState } from "@tanstack/react-router";
import cx from "classnames";
import { useEffect, useRef } from "react";

import { useVersionCheck } from "../hooks/useVersionCheck";

type VersionUpdateBannerProps = {
  className?: string;
};

export function VersionUpdateBanner({ className }: VersionUpdateBannerProps) {
  const { pendingRefresh, isVisible, dismiss, refresh } = useVersionCheck();
  const { location } = useRouterState();
  const prevPathnameRef = useRef(location.pathname);

  // During the grace period, silently reload on any route change
  useEffect(() => {
    const prev = prevPathnameRef.current;
    prevPathnameRef.current = location.pathname;

    if (pendingRefresh && prev !== location.pathname) {
      window.location.reload();
    }
  }, [location.pathname, pendingRefresh]);

  if (!isVisible) return null;

  return (
    <div
      data-testid="version-update-banner"
      role="status"
      aria-live="polite"
      title={`Build ${__APP_VERSION__}`}
      className={cx(
        "fixed bottom-4 right-4 z-[2000] max-w-[340px] animate-block-fade-up rounded-xl p-3",
        className,
      )}
      style={{
        backgroundColor: "#ffffff",
        border: "1px solid var(--gray-6, #d4d1cb)",
        boxShadow: "0 8px 30px rgba(0, 0, 0, 0.18)",
      }}
    >
      <Flex gap="3" align="start">
        <Flex
          align="center"
          justify="center"
          className="mt-0.5 size-8 shrink-0 rounded-full bg-puntt-neutral-gray-4"
        >
          <Sparkle size={18} className="text-puntt-neutral-gray-11" />
        </Flex>

        <Flex direction="column" gap="2" className="min-w-0 flex-1">
          <Flex direction="column" gap="1">
            <Text size="2" weight="medium">
              A new version of Puntt is available
            </Text>
            <Text size="1" className="text-puntt-neutral-gray-11">
              Refresh to load the latest improvements. Your work is saved.
            </Text>
          </Flex>

          <Flex gap="2" justify="end" align="center">
            <Button
              size="1"
              variant="soft"
              color="gray"
              onClick={dismiss}
              data-testid="version-update-banner-dismiss"
            >
              Later
            </Button>
            <Button
              size="1"
              onClick={refresh}
              data-testid="version-update-banner-refresh"
            >
              <ArrowsClockwise size={14} weight="bold" />
              Refresh
            </Button>
          </Flex>
        </Flex>

        <button
          onClick={dismiss}
          className="-mr-1 -mt-1 rounded-full p-1 transition-colors duration-200 hover:bg-puntt-neutral-gray-4"
          aria-label="Dismiss update notification"
          data-testid="version-update-banner-close"
        >
          <X size={14} className="text-puntt-neutral-gray-11" />
        </button>
      </Flex>
    </div>
  );
}
